import { ChevronDownIcon, XMarkIcon } from "@heroicons/react/24/outline";
import { useEffect, useMemo, useRef, useState } from "react";
import Button from "src/components/form/Button";
import Search from "src/components/form/Search";
import TopMenuItemGroup from "src/components/navigation/topNavigation/TopMenuItemGroup";
import TopMenuItemLink from "src/components/navigation/topNavigation/TopMenuItemLink";
import { outerPages } from "src/config/host";
import { searchMenu } from "src/helpers";
import { classNames } from "src/helpers/classNames";
import useClickOutside from "src/hooks/useClickOutside";
import useDevice from "src/hooks/useDevice";
import useUrlQueryParams from "src/hooks/useUrlQueryParams";
import LeftMenuItemGroup from "../leftNavigation/LeftMenuItemGroup";

const TopNavigationMenu = ({ isMenu, setIsMenu, menus = [], simulatedMenus = [], setSimulatedMenus = () => {}, keyword = "", setKeyword = () => {}, handleMenuClick = () => {} }) => {
  const { group: group_id, page: page_id, form: form_id, document: document_id } = useUrlQueryParams();
  const device = useDevice();
  const isMobile = device === "mobile" || device === "mobilePortrait";
  const [toggleId, setToggleId] = useState(null);
  const [showMoreDropdown, setShowMoreDropdown] = useState(false);
  const [maxVisible, setMaxVisible] = useState(7);

  const moreButtonRef = useRef(null);
  const moreDropdownRef = useRef(null);
  const timeoutRef = useRef(null);

  useClickOutside(
    moreDropdownRef,
    () => {
      if (showMoreDropdown) setShowMoreDropdown(false);
    },
    [moreButtonRef],
  );

  const calculateMaxVisible = () => {
    const width = window.innerWidth;
    if (width < 1024) return 100;
    if (width < 1200) return 2;
    if (width < 1350) return 5;
    if (width < 1500) return 5;
    if (width < 1700) return 6;
    if (width < 1900) return 7;
    if (width < 2100) return 8;
    if (width < 2400) return 9;
    if (width < 2600) return 11;
    return 13;
  };

  useEffect(() => {
    const handleResize = () => {
      setMaxVisible(calculateMaxVisible());
    };

    handleResize();
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const { visibleItems, overflowItems } = useMemo(() => {
    if (isMobile) {
      return { visibleItems: simulatedMenus, overflowItems: [] };
    }
    return {
      visibleItems: simulatedMenus.slice(0, maxVisible),
      overflowItems: simulatedMenus.slice(maxVisible),
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [simulatedMenus, maxVisible, device]);

  const isOuterPage = outerPages.includes(window.location.href);

  const searchOnMenus = (keyword = "") => {
    if (!keyword) {
      setSimulatedMenus(menus);
    } else {
      setSimulatedMenus(searchMenu(menus, keyword));
    }
  };

  const groupMenus = menus.filter((menu) => menu.type === "group");
  const individualMenus = menus.filter((menu) => menu.type !== "group");

  const handleClick = (group_id) => {
    setToggleId(group_id ? group_id : "all");
    handleMenuClick();
  };

  const toggleMoreDropdown = () => {
    setShowMoreDropdown(!showMoreDropdown);
  };

  const hasActiveInOverflow = overflowItems.some((item) => {
    if (item.type === "group") {
      if (item?.slug === group_id) return true;

      if (Array.isArray(item.submenu)) {
        return item.submenu.some((sub) => sub?.groupId === group_id);
      }
    }
    if (item?.slug === page_id) return true;

    if (Array.isArray(item.submenu)) {
      return item.submenu.some((sub) => sub?.slug === page_id);
    }
    return false;
  });

  const handleMouseEnter = () => {
    if (timeoutRef.current) clearTimeout(timeoutRef.current);
    setShowMoreDropdown(true);
  };

  const handleMouseLeave = () => {
    timeoutRef.current = setTimeout(() => {
      setShowMoreDropdown(false);
    }, 200);
  };

  return (
    <>
      <div
        className={classNames("fixed left-0 top-0 z-[49] block h-full w-screen bg-black transition-all duration-200 xl:z-[39] xl:hidden", isMenu ? "visible opacity-80" : "invisible opacity-0")}
        onClick={() => setIsMenu(!isMenu)}></div>
      <div className={classNames("fixed left-0 top-0 z-50 flex h-screen w-80 flex-col !bg-leftNavColor bg-white shadow transition-all duration-200 xl:sticky xl:top-16 xl:z-40 xl:h-auto xl:w-full", !isMenu || isOuterPage ? "-translate-x-96" : "translate-x-0")}>
        {isMenu && isMobile && (
          <Button
            buttonIcon={XMarkIcon}
            buttonIconPosition={"right"}
            buttonClasses={"!bg-transparent !absolute top-2 left-full !ml-2 !w-10 !h-10 !px-0 !rounded-full !border-2 !border-white"}
            buttonIconClass={"!w-6 !h-6 !stroke-2"}
            buttonFunction={() => setIsMenu(!isMenu)}
          />
        )}
        {groupMenus.reduce((total, group) => (group?.permissions?.length ? total + group?.permissions?.length : total), individualMenus.length) > 15 && (
          <div className={classNames("block flex-shrink-0 p-2 px-2 py-3 xl:hidden xl:px-0 xl:py-0")}>
            <Search
              value={keyword}
              placeholder="Search"
              onChange={(e) => {
                setKeyword(e.target.value);
                searchOnMenus(e.target.value);
              }}
            />
          </div>
        )}
        <div className={classNames("w-full flex-shrink items-center space-y-2 py-2 xl:flex xl:space-y-0 xl:px-2", isMobile ? " overflow-auto" : "")}>
          <div className={classNames("space-y-2 px-2 xl:flex xl:w-full xl:gap-1 xl:space-y-0", isMobile ? "pb-3" : "")}>
            {visibleItems?.map((item, index) => {
              const itemKey = `visible-${item._id}-${index}`;
              const isOverflowing = overflowItems.some((menu) => menu._id === item._id);
              return item.type === "group" ? (
                <div
                  key={itemKey}
                  className={classNames("transition-all duration-200", isMobile ? "block" : isOverflowing ? "pointer-events-none invisible absolute" : "visible relative block")}>
                  {isMobile ? (
                    <LeftMenuItemGroup
                      data={item}
                      group_id={group_id}
                      page_id={page_id || form_id || document_id}
                      searchTerm={keyword ? keyword.toLowerCase() : ""}
                      handleMenuClick={handleMenuClick}
                    />
                  ) : (
                    <TopMenuItemGroup
                      data={item}
                      group_id={group_id}
                      page_id={page_id || form_id || document_id}
                      searchTerm={keyword ? keyword.toLowerCase() : ""}
                      toggleId={toggleId}
                      setToggleId={setToggleId}
                      handleClick={handleClick}
                    />
                  )}
                </div>
              ) : (
                <div key={itemKey}>
                  <TopMenuItemLink
                    data={item}
                    group_id={group_id}
                    image={item?.image}
                    page_id={page_id || form_id || document_id}
                    searchTerm={keyword ? keyword.toLowerCase() : ""}
                    handleClick={handleClick}
                  />
                </div>
              );
            })}

            {overflowItems.length > 0 && device !== "mobile" && (
              <div
                onMouseEnter={handleMouseEnter}
                onMouseLeave={handleMouseLeave}
                className="relative ml-1">
                <button
                  ref={moreButtonRef}
                  className={classNames(
                    "flex min-h-10 w-full items-center justify-between rounded px-4 py-2 font-medium transition-all  hover:bg-leftNavActiveColor hover:text-leftNavActiveTextColor",
                    showMoreDropdown || hasActiveInOverflow ? "bg-leftNavActiveColor text-leftNavActiveTextColor" : "text-leftNavTextColor",
                  )}
                  onClick={toggleMoreDropdown}>
                  More
                  <ChevronDownIcon className="ml-1 h-4 w-4" />
                </button>

                {showMoreDropdown && (
                  <div
                    ref={moreDropdownRef}
                    className="absolute right-0 z-50 mt-1 w-56 origin-top-right rounded-md bg-leftNavColor shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none">
                    <div className="py-1">
                      {overflowItems.map((item, index) => (
                        <div
                          key={`overflow-${item._id}-${index}`}
                          className="px-2 py-1">
                          {item.type === "group" ? (
                            <TopMenuItemGroup
                              data={item}
                              group_id={group_id}
                              page_id={page_id || form_id || document_id}
                              searchTerm={keyword ? keyword.toLowerCase() : ""}
                              toggleId={toggleId}
                              setToggleId={setToggleId}
                              dropdownPosition="right"
                              handleClick={(id) => {
                                handleClick(id);
                                setShowMoreDropdown(false);
                              }}
                            />
                          ) : (
                            <TopMenuItemLink
                              data={item}
                              group_id={group_id}
                              image={item?.image}
                              page_id={page_id || form_id || document_id}
                              searchTerm={keyword ? keyword.toLowerCase() : ""}
                              handleClick={() => {
                                handleClick();
                                setShowMoreDropdown(false);
                              }}
                            />
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </>
  );
};

export default TopNavigationMenu;
